import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Star, Quote } from "lucide-react"; // <-- Tambah Quote
import { motion, AnimatePresence } from "framer-motion";
import TestimonialModal from "./TestimonialModal";
import { rtdb } from "../../../config/firebase"; // pastikan path sesuai
import { ref, onValue, remove, push, update } from "firebase/database";
import toast, { Toaster } from 'react-hot-toast'; 

// --- Variants Animasi ---
const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
};

// --- Komponen Rating ---
const RatingStars = ({ rating }) => (
    <div className="flex text-yellow-500">
        {[...Array(5)].map((_, i) => (
            <Star 
                key={i} 
                size={18} // Ukuran bintang sedikit lebih besar
                fill={i < rating ? "currentColor" : "none"} 
                className={`transition-colors ${i < rating ? "text-yellow-400" : "text-gray-300"} mr-0.5`} 
                strokeWidth={i < rating ? 0 : 1.5}
            />
        ))}
    </div>
);

// --- Komponen Card Testimonial (Desain Baru) ---
const TestimonialCard = ({ t, onEdit, onDelete }) => (
    <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        layout
        className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100 transition-all duration-300 flex flex-col overflow-hidden hover:shadow-2xl hover:border-green-300"
    >
        {/* Konten Utama */}
        <div className="p-6 flex flex-col flex-grow">
            
            {/* Rating & Header */}
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="font-extrabold text-2xl text-green-700 leading-tight">{t.nama}</h3>
                    <p className="text-gray-500 text-sm font-medium">{t.perusahaan}</p>
                </div>
                <RatingStars rating={t.rating} />
            </div>

            {/* Keterangan/Isi Testimonial dengan Blockquote Style */}
            <div className="relative pl-8 py-3 pr-4 border-l-4 border-teal-500 bg-green-50/50 rounded-r-xl">
                <Quote size={24} className="absolute top-3 -left-3 text-teal-500 opacity-80 rotate-180" />
                <p className="text-gray-800 italic leading-relaxed text-base">
                    {t.keterangan}
                </p>
                
            </div>
            
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-end p-4 space-x-3 bg-gray-50/50 border-t border-gray-100">
            <button onClick={() => onEdit(t)} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-blue-600 bg-blue-100 hover:bg-blue-200 transition-all text-sm font-medium" title="Edit Testimonial">
                <Pencil size={16} /> Edit
            </button>
            <button onClick={() => onDelete(t.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-red-600 bg-red-100 hover:bg-red-200 transition-all text-sm font-medium" title="Hapus Testimonial">
                <Trash2 size={16} /> Hapus
            </button>
        </div>
    </motion.div>
);

// --- Halaman Kelola Testimonial ---
export default function KelolaTestimonial() {
    const [testimonials, setTestimonials] = useState([]);
    const [editingTestimonial, setEditingTestimonial] = useState(null);

    // --- Ambil data real-time dari Firebase ---
    useEffect(() => {
        const testimonialsRef = ref(rtdb, "testimonials");
        const unsubscribe = onValue(testimonialsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const list = Object.keys(data).map((key) => ({ id: key, ...data[key] }));
                setTestimonials(list.reverse());
            } else {
                setTestimonials([]);
            }
        });
        return () => unsubscribe();
    }, []);

    // --- Logika Hapus ---
    const handleDelete = (id) => {
        if (window.confirm("Yakin ingin menghapus testimonial ini?")) {
            remove(ref(rtdb, `testimonials/${id}`))
                .then(() => {
                    toast.success("Testimonial berhasil dihapus!");
                })
                .catch((error) => {
                    console.error("Gagal menghapus:", error);
                    toast.error("Gagal menghapus data.");
                });
        }
    };
    
    // --- Logika Simpan (CREATE/UPDATE) ---
    const handleSave = async (id, data) => {
        setEditingTestimonial(null); 
        
        try {
            if (id) {
                // UPDATE
                await update(ref(rtdb, `testimonials/${id}`), data);
                toast.success("Testimonial berhasil diperbarui!");
            } else {
                // CREATE
                await push(ref(rtdb, "testimonials"), data);
                toast.success("Testimonial baru berhasil ditambahkan!");
            }
        } catch (error) {
            console.error("Gagal menyimpan data:", error);
            toast.error("Gagal menyimpan testimonial.");
        }
    };


    return (
        <div className="relative min-h-screen bg-slate-50">
            <Toaster position="top-right" reverseOrder={false} />
            <main className="relative z-10 p-4 md:p-6">
                <motion.header
                    initial={{ y: -100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="sticky top-4 z-20 bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 mb-8 p-4"
                >
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-500">Kelola</span> Testimonial
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">Tambahkan, edit, atau hapus testimonial dari klien.</p>
                        </div>
                        <button
                            onClick={() => setEditingTestimonial({ id: null, nama: '', perusahaan: '', keterangan: '', rating: 5 })}
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-teal-700 transition-all duration-300 transform hover:scale-105"
                        >
                            <Plus size={18} /> Tambah Testimonial
                        </button>
                    </div>
                </motion.header>

                <motion.div className="max-w-4xl mx-auto space-y-5">
                    <AnimatePresence>
                        {testimonials.length > 0 ? (
                            testimonials.map(t => (
                                <TestimonialCard key={t.id} t={t} onEdit={setEditingTestimonial} onDelete={handleDelete} />
                            ))
                        ) : (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-center py-16 px-6 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 bg-white/60 backdrop-blur-lg"
                            >
                                <Plus size={48} className="mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-700">Belum Ada Testimonial</h3>
                                <p className="mt-1 text-base">Klik tombol "Tambah Testimonial" untuk memulai.</p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </main>

            {/* Modal untuk Edit/Tambah */}
            <AnimatePresence>
                {editingTestimonial && (
                    <TestimonialModal
                        testimonial={editingTestimonial}
                        onClose={() => setEditingTestimonial(null)}
                        onSave={handleSave} 
                        isNew={!editingTestimonial.id}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}